import DomainSummary from "@/components/DomainSummary";
import LiveNetwork from "@/components/LiveNetwork";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AreaChart, Area, ResponsiveContainer, YAxis } from "recharts";
import {
  Cpu, MemoryStick, HardDrive, Globe, Server, AlertTriangle,
  Activity, Zap, Plus, Command, Radio,
} from "lucide-react";
import api from "@/lib/api";
import { PageHeader, Loading, fmtBytes, fmtUptime } from "@/components/common";
import { cn } from "@/lib/utils";

function Gauge({ icon: Icon, label, value, sub, tone }) {
  const color = value > 90 ? "text-rose-400" : value > 70 ? "text-amber-400" : "text-emerald-400";
  return (
    <div className="border border-zinc-800 rounded-lg bg-[#0c0c0e] p-4" data-testid={`gauge-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">
          <Icon className="h-3.5 w-3.5" /> {label}
        </div>
        <span className={cn("font-mono text-sm", color)}>{value}%</span>
      </div>
      <div className="mt-3 h-1.5 w-full rounded-full bg-zinc-800 overflow-hidden">
        <div className={cn("h-full rounded-full", value > 90 ? "bg-rose-500" : value > 70 ? "bg-amber-500" : "bg-emerald-500")} style={{ width: `${value}%` }} />
      </div>
      <div className="mt-2 font-mono text-xs text-zinc-500">{sub}</div>
    </div>
  );
}

function KpiCard({ label, value, unit, accent }) {
  return (
    <div className="border border-zinc-800 rounded-lg bg-[#0c0c0e] p-4">
      <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={cn("font-mono text-2xl mt-2", accent || "text-zinc-50")}>{value ?? "Unavailable"}<span className="text-sm text-zinc-500 ml-1">{unit}</span></div>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [disconnected,setDisconnected]=useState(false);
  const [points, setPoints] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const navigate = useNavigate();

  const load = () => {
    api.get("/dashboard").then(d=>{setData(d);setDisconnected(false);}).catch(() => {setData(null);setDisconnected(true);});
    api.get("/monitoring/points").then((p) => setPoints(p.slice(-60))).catch(() => {});
    api.get("/monitoring/alerts").then(setAlerts).catch(() => {});
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  if (disconnected) return <div role="alert" className="border border-rose-800/40 rounded-lg p-6 text-rose-300">Server Disconnected. Live measurements are unavailable. Reconnecting automatically.</div>;
  if (!data) return <Loading label="Reading system state" />;
  const s = data.system;

  return (
    <div data-testid="dashboard-page">
      <DomainSummary />
      <PageHeader
        title="Dashboard"
        breadcrumb="cannon / overview"
        subtitle={`v${data.version} · ${data.mode} · ${data.base_domain}`}
        actions={
          <>
            <span className={cn("inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-mono",
              data.public_serving ? "border-emerald-500/20 text-emerald-400 bg-emerald-500/5" : "border-rose-500/20 text-rose-400 bg-rose-500/5")}>
              <Radio className="h-3 w-3" /> {data.public_serving ? "SERVING" : "PAUSED"}
            </span>
            <button onClick={() => navigate("/sites?new=1")} data-testid="deploy-btn"
              className="inline-flex items-center gap-2 rounded-md bg-zinc-100 text-zinc-900 px-3 py-1.5 text-sm font-medium hover:bg-white transition-colors">
              <Plus className="h-4 w-4" /> Deploy Site / App
            </button>
          </>
        }
      />

      <p className="text-xs text-zinc-500 mb-3 font-mono">{s.server_id} · {s.hostname} · {s.source} · measured {new Date(s.measured_at).toLocaleTimeString()}</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <Gauge icon={Cpu} label="CPU" value={s.cpu_percent} sub={`uptime ${fmtUptime(s.uptime_seconds)}`} />
        <Gauge icon={MemoryStick} label="RAM" value={s.mem_percent} sub={`${s.mem_used_mb} / ${s.mem_total_mb} MB`} />
        <Gauge icon={HardDrive} label="Disk" value={s.disk_percent} sub={`${s.disk_used_gb} / ${s.disk_total_gb} GB`} />
      </div>

      <p className="text-xs text-zinc-600 mb-3">{data.traffic_scope}</p>
      <LiveNetwork />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <KpiCard label="Sites" value={data.site_count} accent="text-sky-400" />
        <KpiCard label="Node Running" value={data.node_running} accent="text-emerald-400" />
        <KpiCard label="Requests Today" value={data.requests_today} />
        <KpiCard label="Errors Today" value={data.errors_today} accent={data.errors_today ? "text-rose-400" : "text-zinc-50"} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-zinc-800 rounded-lg bg-[#0c0c0e] p-4">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-4">
            <Activity className="h-3.5 w-3.5" /> CPU / RAM · last {points.length} samples
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={points}>
              <defs>
                <linearGradient id="cpu" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0070f3" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#0070f3" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="mem" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <YAxis domain={[0, 100]} hide />
              <Area type="monotone" dataKey="cpu" stroke="#0070f3" strokeWidth={1.5} fill="url(#cpu)" isAnimationActive={false} />
              <Area type="monotone" dataKey="mem" stroke="#10b981" strokeWidth={1.5} fill="url(#mem)" isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
          <div className="flex gap-4 mt-2 font-mono text-xs">
            <span className="text-[#0070f3]">■ CPU</span><span className="text-emerald-400">■ RAM</span>
          </div>
        </div>

        <div className="border border-zinc-800 rounded-lg bg-[#0c0c0e] p-4">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-4">
            <AlertTriangle className="h-3.5 w-3.5" /> Alerts
          </div>
          {alerts.length === 0 ? (
            <div className="text-sm text-zinc-600 font-mono py-6 text-center">All clear</div>
          ) : (
            <div className="space-y-2" data-testid="alerts-list">
              {alerts.map((a, i) => (
                <div key={i} className={cn("rounded-md border px-3 py-2 text-xs font-mono",
                  a.level === "error" ? "border-rose-500/20 bg-rose-500/5 text-rose-300" : "border-amber-500/20 bg-amber-500/5 text-amber-300")}>
                  <button onClick={()=>navigate(`/console?service=${a.service||"server"}&command=logs`)}>{a.message} → Investigate</button>
                </div>
              ))}
            </div>
          )}
          <div className="mt-4 pt-4 border-t border-zinc-800 text-xs text-zinc-500 font-mono flex items-center gap-1">
            <Command className="h-3 w-3" />K to search · storage {fmtBytes(data.storage_bytes)}
          </div>
        </div>
      </div>
    </div>
  );
}
